import { toJS } from "mobx";
import { useEffect, useMemo } from "react";

import { usePlayground } from "../playground-store";

import { useParseInputVariables } from "@/queries";
import { useAuth } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { useDebounce } from "@/hooks/debounce";
import { PromptBlueprint } from "@/types";
import { displayToast } from "@/utils/toast";

const useAvailableInputVariables = () => {
  const playground = usePlayground();
  const authContext = useAuth();
  const userToken = authContext!.userToken!;

  // Serialize the blueprint to a JSON string for stable value comparison.
  // useDebounce compares strings by value (not reference), so the debounce
  // timer only resets when the actual content changes, not on every re-render.
  const blueprintJson = JSON.stringify(toJS(playground.blueprint));
  const debouncedJson = useDebounce(blueprintJson, 200);
  const debouncedBlueprint = useMemo(
    () => JSON.parse(debouncedJson) as PromptBlueprint,
    [debouncedJson],
  );

  const parseInputVariables = useParseInputVariables(userToken, debouncedBlueprint);

  useEffect(() => {
    playground.setIsDetectingInputVariables(
      parseInputVariables.isLoading || parseInputVariables.isFetching,
    );
  }, [parseInputVariables.isFetching, parseInputVariables.isLoading, playground]);

  useEffect(() => {
    const { data } = parseInputVariables;
    if (!parseInputVariables.isLoading && data) {
      if (data.success) {
        playground.messages.forEach(message => message.setParsingError(""));
        playground.setDetectedInputVariables(data.input_variables.filter(v => v));
        playground.setIsDetectingInputVariables(false);
      } else {
        if (typeof data.message === "string") {
          displayToast(data.message, ToastType.error, { id: `parse-input-vars-${data.message}` });
          playground.setIsDetectingInputVariables(false);
          return;
        }
        const fullMessage = data.message[0].msg || "";
        const errorMessages = fullMessage.split("\n");
        errorMessages.shift();

        // Build a map of message index -> error detail
        const errorsByIndex = new Map<number, string>();
        errorMessages.forEach(message => {
          const index = Number(message.match(/index\s+(\d+):/)?.[1] || "0");
          const colonIndex = message.indexOf(":");
          const errorDetail =
            colonIndex !== -1 ? message.substring(colonIndex + 1).trim() : message.trim();
          errorsByIndex.set(index, errorDetail);
        });

        // Only update messages whose error state actually changed,
        // so dismissed errors on unchanged messages stay dismissed.
        playground.messages.forEach((message, index) => {
          const newError = errorsByIndex.get(index) || "";
          message.setParsingError(newError);
        });
        playground.setIsDetectingInputVariables(false);
      }
    }
  }, [parseInputVariables, playground]);
};

export default useAvailableInputVariables;
